import { useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { YStack, XStack, View, Text } from "tamagui";
import { RiArrowLeftSLine, RiMapPin2Line, RiDeleteBinLine, RiHome5Line } from "react-icons/ri";

import Header from "@components/layout/Header";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import { colors } from "@styles/colors";
import { IdentityContext } from "@utils/store/user/useUser";

import SectionLabel from "./components/ui/SectionLabel";
import ListContainer from "./components/ui/ListContainer";
import BottomSheet from "./components/ui/BottomSheet";
import { track } from "@lib/tracker";

const PRESET_NICKNAMES = ["本宅", "辦公室", "工作室", "店面", "倉庫", "車庫", "其他"];

const inputStyle: React.CSSProperties = {
  width: "100%",
  border: "none",
  outline: "none",
  fontSize: 15,
  color: colors.text,
  background: "transparent",
  fontFamily: "inherit",
  padding: 0,
};

/**
 * 送貨地址新增 / 編輯。切版階段：欄位以本地 state 管理、初值留空；之後接 API 帶入既有地址與儲存。
 * 精準位置目前為佔位區，後續可整合既有 MapPicker 元件。
 */
export const AddressEdit = () => {
  const navigate = useNavigate();
  const { contextId, addressId } = useParams();
  const location = useLocation();
  const ctx = (location.state as { context?: IdentityContext } | null)?.context ?? null;
  const back = () => navigate(`/account/detail/${contextId}`, { state: { context: ctx } });

  const isNew = !addressId;
  const vendor = ctx?.primary_supplier?.supplier_name ?? "瓦斯行";

  const [nickname, setNickname] = useState("");
  const [fullAddr, setFullAddr] = useState("");
  const [floor, setFloor] = useState("");
  const [contact, setContact] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [showNicknameSheet, setShowNicknameSheet] = useState(false);
  const [showDeleteSheet, setShowDeleteSheet] = useState(false);

  const canSave = nickname.trim().length > 0 && fullAddr.trim().length > 0;

  return (
    <YStack height="100dvh" width="100vw" background={colors.bg}>
      <Header>
        <XStack ai="center" onPress={back} cursor="pointer" mb="$3">
          <View mr="$2">
            <RiArrowLeftSLine size={20} color={colors.light} />
          </View>
          <Text color={colors.light} fontWeight="bold" fontSize={16}>
            返回
          </Text>
        </XStack>
        <Text color={colors.light} fontSize={22} fontWeight="800" mb="$1.5">
          {isNew ? "新增送貨地址" : "編輯送貨地址"}
        </Text>
        <Text color="rgba(255,255,255,0.85)" fontSize={12}>
          {vendor} · 送貨員依此地址配送
        </Text>
      </Header>

      <RadiusContentWrapper space="$4">
        {/* 地址暱稱 */}
        <YStack>
          <SectionLabel title="地址暱稱" />
          <YStack bg={colors.light} br={12} px="$3.5" py="$3">
            <XStack ai="center" gap="$2.5">
              <input value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="例如：本宅、辦公室" style={{ ...inputStyle, fontWeight: 600 }} />
              <XStack br={8} px="$2.5" py="$1.5" bg={`${colors.primary}15`} cursor="pointer" flexShrink={0} onPress={() => setShowNicknameSheet(true)}>
                <Text fontSize={11} fontWeight="700" color={colors.primary}>
                  常用
                </Text>
              </XStack>
            </XStack>
          </YStack>
        </YStack>

        {/* 詳細地址 */}
        <YStack>
          <SectionLabel title="詳細地址" />
          <ListContainer>
            <YStack px="$3.5" py="$3" bbw={1} bbc={colors.borderLight}>
              <Text fontSize={11} color={colors.hint} mb="$1.5">
                完整地址
              </Text>
              <input value={fullAddr} onChange={(e) => setFullAddr(e.target.value)} placeholder="請輸入完整地址（縣市 / 區 / 路街 / 號）" style={{ ...inputStyle, fontSize: 14 }} />
            </YStack>
            <YStack px="$3.5" py="$3">
              <Text fontSize={11} color={colors.hint} mb="$1.5">
                樓層 / 單位（選填）
              </Text>
              <input value={floor} onChange={(e) => setFloor(e.target.value)} placeholder="3 樓 / B 棟" style={{ ...inputStyle, fontSize: 14 }} />
            </YStack>
          </ListContainer>
        </YStack>

        {/* 精準位置（佔位） */}
        <YStack>
          <SectionLabel title="精準位置" right={<Text fontSize={10} color={colors.hint}>待整合地圖</Text>} />
          <YStack bg={colors.light} br={12} ov="hidden">
            <YStack h={180} bg={colors.bg} ai="center" jc="center" gap="$2">
              <RiMapPin2Line size={28} color={colors.gray200} />
              <Text fontSize={12} color={colors.hint}>
                地圖定位（後續整合 MapPicker）
              </Text>
            </YStack>
            <XStack ai="center" gap="$2" px="$3.5" py="$3" btw={1} btc={colors.borderLight}>
              <RiMapPin2Line size={14} color={colors.sub} />
              <Text fontSize={12} color={colors.sub}>
                用地址自動定位 · 可拖曳微調實際入口
              </Text>
            </XStack>
          </YStack>
        </YStack>

        {/* 聯絡人 */}
        <YStack>
          <SectionLabel title="送貨聯絡人" />
          <ListContainer>
            <YStack px="$3.5" py="$3" bbw={1} bbc={colors.borderLight}>
              <Text fontSize={11} color={colors.hint} mb="$1.5">
                姓名
              </Text>
              <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="聯絡人姓名" style={{ ...inputStyle, fontSize: 14 }} />
            </YStack>
            <YStack px="$3.5" py="$3">
              <Text fontSize={11} color={colors.hint} mb="$1.5">
                電話
              </Text>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder="09xx-xxx-xxx" style={{ ...inputStyle, fontSize: 14 }} />
            </YStack>
          </ListContainer>
        </YStack>

        {/* 備註 */}
        <YStack>
          <SectionLabel title="配送備註（選填）" />
          <YStack bg={colors.light} br={12} px="$3.5" py="$3">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="例如：大門密碼 1234、瓦斯桶請放後巷"
              rows={3}
              style={{ ...inputStyle, fontSize: 13, lineHeight: "1.6", resize: "none" }}
            />
          </YStack>
        </YStack>

        {/* 刪除（編輯時） */}
        {!isNew && (
          <XStack bg={colors.light} bw={1} bc={`${colors.danger}30`} br={12} px="$4" py="$3.5" ai="center" jc="center" gap="$1.5" cursor="pointer" onPress={() => setShowDeleteSheet(true)}>
            <RiDeleteBinLine size={15} color={colors.danger} />
            <Text fontSize={13} fontWeight="600" color={colors.danger}>
              刪除此地址
            </Text>
          </XStack>
        )}
      </RadiusContentWrapper>

      {/* Sticky 儲存 */}
      <YStack bg={colors.light} px="$5" pt="$3" pb="$6" btw={1} btc={colors.border} style={{ position: "sticky", bottom: 0 }}>
        <XStack
          ai="center"
          jc="center"
          br={12}
          py="$3.5"
          className={canSave ? "primary-bg-gradient" : undefined}
          bg={canSave ? undefined : colors.gray100}
          cursor={canSave ? "pointer" : "default"}
          opacity={canSave ? 1 : 0.7}
          onPress={() => {
            if (canSave) {
              if (isNew) track("address_add_submit", {});
              back();
            }
          }}>
          <Text fontSize={15} fontWeight="700" color={canSave ? colors.light : colors.hint}>
            {isNew ? "新增地址" : "儲存變更"}
          </Text>
        </XStack>
      </YStack>

      {/* 常用暱稱 sheet */}
      <BottomSheet open={showNicknameSheet} onClose={() => setShowNicknameSheet(false)}>
        <YStack px="$5" pb="$3" bbw={1} bbc={colors.borderLight}>
          <Text fontSize={16} fontWeight="800" color={colors.text}>
            選擇常用暱稱
          </Text>
        </YStack>
        <YStack px="$4" py="$2" pb="$6">
          {PRESET_NICKNAMES.map((p, i) => (
            <XStack
              key={p}
              ai="center"
              gap="$3"
              px="$2"
              py="$3"
              bbw={i === PRESET_NICKNAMES.length - 1 ? 0 : 1}
              bbc={colors.borderLight}
              cursor="pointer"
              onPress={() => {
                setNickname(p);
                setShowNicknameSheet(false);
              }}>
              <View w={32} h={32} br={8} bg={`${colors.primary}15`} ai="center" jc="center" flexShrink={0}>
                <RiHome5Line size={15} color={colors.primary} />
              </View>
              <Text fontSize={14} fontWeight="600" color={colors.text}>
                {p}
              </Text>
            </XStack>
          ))}
        </YStack>
      </BottomSheet>

      {/* 刪除確認 sheet */}
      <BottomSheet open={showDeleteSheet} onClose={() => setShowDeleteSheet(false)}>
        <YStack px="$5" pb="$6" ai="center">
          <View w={56} h={56} br={28} bg={colors.bgRed} ai="center" jc="center" mb="$3.5">
            <RiDeleteBinLine size={26} color={colors.danger} />
          </View>
          <Text fontSize={18} fontWeight="800" color={colors.text} ta="center" mb="$2">
            刪除「{nickname || "此地址"}」？
          </Text>
          <Text fontSize={13} color={colors.sub} ta="center" mb="$5" lineHeight={20}>
            刪除後無法叫桶到此地址，歷史訂單仍保留。
          </Text>
          <XStack gap="$2.5" w="100%">
            <XStack f={1} ai="center" jc="center" bg={colors.bg} br={12} py="$3.5" cursor="pointer" onPress={() => setShowDeleteSheet(false)}>
              <Text fontSize={14} fontWeight="600" color={colors.sub}>
                取消
              </Text>
            </XStack>
            <XStack f={1} ai="center" jc="center" bg={colors.danger} br={12} py="$3.5" cursor="pointer" onPress={() => { setShowDeleteSheet(false); back(); }}>
              <Text fontSize={14} fontWeight="700" color={colors.light}>
                確定刪除
              </Text>
            </XStack>
          </XStack>
        </YStack>
      </BottomSheet>
    </YStack>
  );
};

export default AddressEdit;
